import React, { useState } from 'react';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { validateUnitInput } from '../bulkImport/validation';

interface UnitFormProps {
  initialData?: any;
  propertyId?: number | string;
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
}

export const UnitForm: React.FC<UnitFormProps> = ({ initialData, propertyId, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState(initialData || { property_id: propertyId });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (key: string, val: string) => setFormData((p: any) => ({ ...p, [key]: val }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors = validateUnitInput(formData);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    setLoading(true);
    try {
      const payload: any = {
        ...formData,
        property_id: propertyId || formData.property_id,
      };
      // Remove 'name' — not a v2_units column (use display_name/unit_number instead)
      delete payload.name;
      // Generate unit_uuid for new records (required by Postgres uuid column)
      if (!initialData && !payload.unit_uuid) {
        payload.unit_uuid = crypto.randomUUID();
      }
      await onSubmit(payload);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input 
        label="Unit Number" 
        value={formData.unit_number || ''} 
        onChange={(e) => set('unit_number', e.target.value)} 
        placeholder="e.g., 101, 2A, etc."
        required 
        error={errors.unit_number}
      />
      <Input 
        label="Floor (optional)" 
        value={formData.floor || ''} 
        onChange={(e) => set('floor', e.target.value)} 
        placeholder="e.g., 1, 2, Ground, etc."
      />
      <Input 
        label="Display Name (optional)" 
        value={formData.display_name || ''} 
        onChange={(e) => set('display_name', e.target.value)} 
        placeholder="e.g., Penthouse, Garden Suite, etc."
      />
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
        <select 
          value={formData.status || 'active'} 
          onChange={(e) => set('status', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>
      <div className="flex gap-3 justify-end mt-6 pt-4 border-t">
        <Button variant="secondary" type="button" onClick={onCancel}>Cancel</Button>
        <Button type="submit" loading={loading}>{initialData ? 'Update' : 'Create'} Unit</Button>
      </div>
    </form>
  );
};
